import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCachedSession } from '@/lib/sessionCache'
import type { User } from '@supabase/supabase-js'

/**
 * 🛡️ Auth Guard Hook
 * Korumalı sayfalarda kullanıcı oturumunu doğrular.
 * Oturum yoksa /login sayfasına yönlendirir.
 *
 * ⚠️ getSession() yerine cached session kullanır — token sunucuda doğrulanır.
 *
 * @param options.requiredRole - Opsiyonel rol kontrolü (örn: 'super_admin')
 * @returns { user, loading } - Doğrulanmış kullanıcı ve yükleme durumu
 */
export function useAuthGuard(options?: { requiredRole?: string }) {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Ağ hatalarında veya yavaş Supabase yanıtlarında takılı kalmamak için timeout eklendi (Apple Review)
        const timeoutPromise = new Promise<{ session: null; user: null }>((_, reject) => 
          setTimeout(() => reject(new Error('Auth request timed out')), 10000)
        )

        // ✅ Yükleme sürelerini düşürmek için getSession yerine cached getSession kullanıldı 
        const response = await Promise.race([
          getCachedSession(),
          timeoutPromise
        ])

        const { user } = response

        if (!user) {
          navigate('/login', { replace: true })
          return
        }

        // Rol kontrolü (opsiyonel)
        if (options?.requiredRole) {
          const role = user.app_metadata?.role
          if (role !== options.requiredRole) {
            navigate('/dashboard', { replace: true })
            return
          }
        }

        setUser(user)
      } catch {
        navigate('/login', { replace: true })
      } finally {
        setLoading(false)
      }
    }

    // ✅ DEV-1 Fix: Developer Bypass (Local ortamda giriş yapmadan gezebilmek için)
    if (import.meta.env.DEV && typeof window !== 'undefined' && localStorage.getItem('dev-mode-bypass') === 'true') {
      setUser({
        id: 'dev-user',
        aud: 'authenticated',
        role: 'authenticated',
        email: 'dev@example.com',
        phone: '',
        app_metadata: { provider: 'dev' },
        user_metadata: { full_name: 'Developer' },
        created_at: new Date().toISOString(),
      } as User)
      setLoading(false)
      return
    }

    checkAuth()
  }, [navigate, options?.requiredRole])

  return { user, loading }
}
